import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "../ui/Modal";
import * as XLSX from "xlsx";
import {
  fetchAllergens,
  addAllergen,
  editAllergen,
  deleteAllergen,
} from "../../services/menuApi";
import "./AllergenManagerModal.css";

const emptyForm = {
  id: "",
  title: "",
  description: "",
  icon: "",
};

export default function AllergenManagerModal({
  open,
  tenantServerUrl,
  onClose,
  onDownloadTemplate,
  onImport,
}) {
  const [allergens, setAllergens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!tenantServerUrl) return;
    setLoading(true);
    setErr("");
    try {
      const rows = await fetchAllergens(tenantServerUrl);
      setAllergens(rows);
    } catch (e) {
      setErr(e?.message || "Failed to load allergens");
    } finally {
      setLoading(false);
    }
  }, [tenantServerUrl]);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm);
    setEditingId(null);
    setErr("");
    load();
  }, [open, load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      id: row.id,
      title: row.title || "",
      description: row.description || "",
      icon: row.icon && row.icon !== "none.jpg" ? row.icon : "",
    });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const title = String(form.title || "").trim();
    if (!title) {
      setErr("Allergen name is required");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const payload = {
        title,
        description: String(form.description || "").trim(),
        icon: String(form.icon || "").trim() || "none.jpg",
      };
      if (editingId) {
        await editAllergen(tenantServerUrl, { ...payload, id: editingId });
      } else {
        await addAllergen(tenantServerUrl, payload);
      }
      resetForm();
      await load();
    } catch (ex) {
      setErr(ex?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    const used = Number(row.item_count || 0);
    const ok = window.confirm(
      used
        ? `Delete allergen "${row.title}"?\nIt is currently on ${used} menu item${used === 1 ? "" : "s"} and will be removed from them.`
        : `Delete allergen "${row.title}"?`
    );
    if (!ok) return;
    setSaving(true);
    setErr("");
    try {
      await deleteAllergen(tenantServerUrl, { id: row.id });
      if (editingId === row.id) resetForm();
      await load();
    } catch (ex) {
      setErr(ex?.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const handleImportClick = () => fileRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onImport) return;
    setSaving(true);
    setErr("");
    try {
      await onImport(file);
      await load();
    } catch (ex) {
      setErr(ex?.message || "Import failed");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    if (!allergens.length) {
      setErr("No allergens to export");
      return;
    }
    const rows = allergens.map((row) => ({
      title: row.title,
      description: row.description,
      icon: row.icon && row.icon !== "none.jpg" ? row.icon : "",
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Allergens");
    XLSX.writeFile(wb, "menu_allergens.xlsx");
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose?.()}
      title="Allergens"
      width={860}
      footer={
        <div className="allergen-manager-footer">
          <button type="button" className="allergen-manager-btn" onClick={onClose} disabled={saving}>
            Close
          </button>
        </div>
      }
    >
      <p className="allergen-manager-help">
        Manage the allergen library, then attach allergens to each menu item from the item editor.
        Dining apps show these on the item so guests can see gluten, milk, nuts, and similar warnings.
      </p>

      <div className="allergen-manager-file-actions">
        {onDownloadTemplate && (
          <button
            type="button"
            className="allergen-manager-btn"
            onClick={onDownloadTemplate}
            disabled={saving}
          >
            Template
          </button>
        )}
        {onImport && (
          <>
            <button
              type="button"
              className="allergen-manager-btn"
              onClick={handleImportClick}
              disabled={saving}
            >
              Import
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".json,.csv,.xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleImportFile}
            />
          </>
        )}
        <button
          type="button"
          className="allergen-manager-btn"
          onClick={handleExport}
          disabled={saving || loading}
        >
          Export
        </button>
      </div>

      {!!err && <div className="allergen-manager-error">{err}</div>}

      <form className="allergen-manager-form" onSubmit={submit}>
        <label>
          <span>Name</span>
          <input
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="e.g., Milk"
          />
        </label>
        <label>
          <span>Icon filename</span>
          <input
            value={form.icon}
            onChange={(e) => set("icon", e.target.value)}
            placeholder="Milk.jpg"
          />
        </label>
        <label className="allergen-manager-desc">
          <span>Description</span>
          <input
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Where this allergen is commonly found"
          />
        </label>
        <div className="allergen-manager-form-actions">
          {editingId && (
            <button type="button" className="allergen-manager-btn" onClick={resetForm} disabled={saving}>
              Cancel edit
            </button>
          )}
          <button type="submit" className="allergen-manager-btn-primary" disabled={saving}>
            {saving ? "Saving…" : editingId ? "Update allergen" : "Add allergen"}
          </button>
        </div>
      </form>

      <div className="allergen-manager-list-wrap">
        {loading ? (
          <div className="allergen-manager-empty">Loading…</div>
        ) : allergens.length === 0 ? (
          <div className="allergen-manager-empty">No allergens yet. Add one above.</div>
        ) : (
          <table className="allergen-manager-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Icon</th>
                <th>Used on</th>
                <th>Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {allergens.map((row) => (
                <tr key={row.id} className={editingId === row.id ? "is-editing" : ""}>
                  <td>{row.title}</td>
                  <td>{row.icon && row.icon !== "none.jpg" ? row.icon : "—"}</td>
                  <td>{row.item_count || 0}</td>
                  <td>{row.description}</td>
                  <td className="allergen-manager-row-actions">
                    <button type="button" onClick={() => startEdit(row)} disabled={saving}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => remove(row)}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}
